import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography
} from '@mui/material';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import ModalGeneric from '../../Components/generic/ModalGeneric';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import { InformacoesCliente, ParamsCadastrarNovoCliente } from '../../types/EdClienteTypes';
import ModalConfirmarCadastro from './ModalConfirmarCadastro';
import LogoEmpresa from '../../Components/generic/LogoEmpresa';
import apiCliente from '../../api/apiCliente';
import apiAutenticacao from '../../api/apiAutenticacao';
import EdNumberField from '../../Components/generic/EdNumberField';
import { useSessao } from '../../redux/sessao/sessaoHooks';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { getErroSenha } from '../../helper';

type Props = {
  open: boolean;
  onClose: () => void;
};

const ModalCadastroUsuario: React.FC<Props> = ({ open, onClose }) => {
  const classes = useStyles();

  const [modoCadastro, setModoCadastro] = useState<'inicial' | 'cadastro'>('inicial');

  const [loading, setLoading] = useState(false);
  const [documento, setDocumento] = useState('');
  const [clienteEncontrado, setClienteEncontrado] = useState<InformacoesCliente | null>(null);
  const [openModalConfirmarCadastro, setOpenModalConfirmarCadastro] = useState(false);

  const [apiEmailOk, setApiEmailOk] = useState(false);
  const [apiZapOk, setApiZapOk] = useState(false);
  const [semOpcaoDisponivel, setSemOpcaoDisponivel] = useState(false);
  const [isPermiteCadastro, setIsPermiteCadastro] = useState(false);

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [dddCelular, setDddCelular] = useState('');
  const [endereco, setEndereco] = useState('');
  const [numeroEndereco, setNumeroEndereco] = useState<number>(0);

  const [bairro, setBairro] = useState('');
  const [cidade, setCidade] = useState('');
  const [cep, setCep] = useState('');
  const [uf, setUf] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);

  const [, login] = useSessao();

  useEffect(() => {
    const getPermiteCadastroCliente = async () => {
      try {
        const resposta = await apiAutenticacao.getPermiteCadastroCliente();
        setIsPermiteCadastro(resposta);
      } catch (error) {
        console.error(error);
      }
    };
    getPermiteCadastroCliente();
  }, []);

  const verificarCliente = async () => {
    if (!documento.trim()) {
      toast.error('Informe o CPF ou CNPJ');
      return;
    }

    setLoading(true);
    try {
      const res = await apiCliente.buscarInformacoesCliente(documento);

      if (res && res.nome) {
        const [resEmail, resZap] = await Promise.all([
          apiAutenticacao.getStatusEmail(),
          apiAutenticacao.getStatusWhatsApp()
        ]);

        setApiEmailOk(resEmail);
        setApiZapOk(resZap?.includes('OK'));

        setClienteEncontrado(res);
        setOpenModalConfirmarCadastro(true);
        setLoading(false);
      } else {
        if (isPermiteCadastro) {
          setModoCadastro('cadastro');
        } else {
          setSemOpcaoDisponivel(true);
        }
        setLoading(false);
      }
    } catch (erro: any) {
      setLoading(false);
      toast.error(erro.response?.data || 'Erro ao verificar cliente');
    }
  };

  const cadastrarNovoCliente = async () => {
    if (
      !nome.trim() ||
      !email.trim() ||
      !telefone.trim() ||
      !endereco.trim() ||
      !bairro.trim() ||
      !cidade.trim()
    ) {
      toast.error('Preencha todos os campos para o cadastro');
      return;
    }

    const erroSenha = getErroSenha(senha.trim());
    if (erroSenha) {
      toast.error(erroSenha);
      return;
    }

    setLoading(true);
    try {
      const params: ParamsCadastrarNovoCliente = {
        nome,
        email,
        telefone,
        dddCelular,
        endereco,
        numeroEndereco,
        bairro,
        cidade,
        cep,
        uf,
        senha,
        documento
      };

      const novoClienteUsuario = await apiAutenticacao.cadastrarNovoCliente(params);
      if (novoClienteUsuario) {
        login(novoClienteUsuario.login, novoClienteUsuario.senha);
      }

      toast.success('Cadastro realizado com sucesso!');

      onClose();
    } catch (erro: any) {
      console.error('Erro ao cadastrar cliente', erro);
      toast.error('Falha ao realizar o cadastro. Por favor, tente novamente.');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <ModalGeneric open={open} onClose={onClose}>
        <Box sx={{ width: '100%', textAlign: 'center' }}>
          <h2>Verificando informações ...</h2>
          <LinearProgress />
        </Box>
      </ModalGeneric>
    );
  }

  return (
    <ModalGeneric open={open} onClose={onClose}>
      {modoCadastro === 'inicial' && (
        <>
          <LogoEmpresa />

          <div className={classes.form}>
            <Typography component={'div'} variant="h5" color={'#428CFF'}>
              Seja Bem Vindo!
            </Typography>

            <Typography
              component={'div'}
              variant="h6"
              color={'#428CFF'}
              style={{ marginBottom: '20px' }}
            >
              Registre-se
            </Typography>

            <TextField
              label="CPF / CNPJ"
              variant="outlined"
              value={documento}
              onChange={(e) => setDocumento(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <HistoryEduIcon />
                  </InputAdornment>
                )
              }}
              sx={{
                width: {
                  xs: '100%',
                  sm: 250
                },
                marginBottom: 2
              }}
            />

            <Grid item xs={12} style={{ textAlign: 'center' }}>
              <Button variant="contained" onClick={verificarCliente}>
                <Typography fontSize={12}>Cadastrar</Typography>
              </Button>
            </Grid>
          </div>
        </>
      )}

      {/* Se cliente não existe, mostra os demais campos para cadastro */}
      {modoCadastro === 'cadastro' && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Nome"
                fullWidth
                variant="outlined"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                inputProps={{ maxLength: 60 }}
              />
            </Grid>

            <Grid item xs={10}>
              <TextField
                label="Endereço"
                fullWidth
                variant="outlined"
                value={endereco}
                onChange={(e) => setEndereco(e.target.value)}
                inputProps={{ maxLength: 60 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <EdNumberField
                label="Numero"
                fullWidth
                variant="outlined"
                value={numeroEndereco}
                onChange={(value) => setNumeroEndereco(value)}
                maxLength={5}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <TextField
                label="Bairro"
                fullWidth
                variant="outlined"
                value={bairro}
                onChange={(e) => setBairro(e.target.value)}
                inputProps={{ maxLength: 60 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <TextField
                label="Cidade"
                fullWidth
                variant="outlined"
                value={cidade}
                onChange={(e) => setCidade(e.target.value)}
                inputProps={{ maxLength: 60 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="UF"
                fullWidth
                variant="outlined"
                value={uf}
                onChange={(e) => setUf(e.target.value)}
                inputProps={{ maxLength: 2 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="CEP"
                fullWidth
                variant="outlined"
                value={cep}
                onChange={(e) => setCep(e.target.value)}
                inputProps={{ maxLength: 8 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="DDD"
                fullWidth
                variant="outlined"
                value={dddCelular}
                onChange={(e) => setDddCelular(e.target.value)}
                inputProps={{ maxLength: 2 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Telefone"
                fullWidth
                variant="outlined"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                inputProps={{ maxLength: 9 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                fullWidth
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                inputProps={{ maxLength: 54 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Senha"
                fullWidth
                variant="outlined"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                type={mostrarSenha ? 'text' : 'password'}
                inputProps={{ maxLength: 10 }}
                helperText="Mínimo de 6 caracteres, com letras e números"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setMostrarSenha(!mostrarSenha)}
                      >
                        {mostrarSenha ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>

          <Grid item xs={12} style={{ textAlign: 'center', marginTop: 24 }}>
            <Button variant="contained" color="primary" onClick={cadastrarNovoCliente}>
              <Typography fontSize={12}>Finalizar Cadastro</Typography>
            </Button>
          </Grid>
        </>
      )}

      {clienteEncontrado && openModalConfirmarCadastro && (
        <ModalConfirmarCadastro
          open={openModalConfirmarCadastro}
          dadosCliente={clienteEncontrado}
          apiEmailOk={apiEmailOk}
          apiZapOk={apiZapOk}
          onClose={() => {
            setOpenModalConfirmarCadastro(false);
            onClose();
          }}
        />
      )}

      {semOpcaoDisponivel && (
        <ModalGeneric open={semOpcaoDisponivel} onClose={() => setSemOpcaoDisponivel(false)}>
          <LogoEmpresa />
          <div style={{ margin: 15, textAlign: 'center' }}>
            <Typography fontWeight="bold">
              Não foram encontradas opções em seu cadastro para que seja possível verificar o mesmo.
              Entre em contato com a empresa, para regularizar seu cadastro.
            </Typography>
          </div>
        </ModalGeneric>
      )}
    </ModalGeneric>
  );
};

export default ModalCadastroUsuario;

const useStyles = makeStyles((theme) =>
  createStyles({
    input: {
      color: '#fff',
      borderColor: '#fff',
      minWidth: '250px',
      marginBottom: '30px'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: 30,
      maxHeight: '70vh',
      overflowY: 'auto',
      width: '100%',
      padding: '0 16px',
      boxSizing: 'border-box',
      alignItems: 'center'
    }
  })
);
function usePerfilSessao() {
  throw new Error('Function not implemented.');
}
